/**
 * Landing — the storefront's default route: the published-events poster
 * grid. Mounted by storefront.view.ts's "storefront" area; selection
 * (a card click) hands off to the buyer flow's next step. Ticket selection
 * and checkout are event-page.view.ts's — this view carries each published
 * event as one card, for browsing only.
 *
 * An archived card is the one exception, and it goes nowhere: an ended event
 * has nothing to sell, so routing to the buying page would land the buyer on
 * a shop with no purchasable ticket. It opens `<event-description>` — the
 * reading the buying page's own header already opens — over the page the
 * buyer is browsing, so closing it returns them to the archive they opened it
 * from. The refund chip on such a card still routes: it stops the card's own
 * click, so the chip opens the return page and the card body opens the
 * reading.
 *
 * Also mounts `<legal-footer>` unconditionally at the foot of its own
 * content column — the storefront's sole surface carrying it.
 */
import { area } from "@mhmo91/schmancy";
import { organizationPathPrefix } from "../../workspace/organization.state.ts";
// Side-effect: registers <schmancy-details>, the archive's disclosure. Named
// here rather than left to the app's auto-import plugin, whose scan is rooted
// at each app's own src and never reaches this package — and no app src
// carries the tag: undefined, it still lays its children out and paints
// nothing, so the archive would open onto a blank strip, silently.
import "@mhmo91/schmancy/details";
import { curtain, overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { Event } from "@momo/types";
import { html } from "lit";
import { choose } from "lit/directives/choose.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { takeUntil } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { AppSettings } from "../../settings/app-settings.view.ts";
import "../../legal/legal-footer.view.ts";
import "../../workspace/brand-logo.view.ts";
import { brandName, workspaceCard } from "../../workspace/workspace.state.ts";
import { EventDescriptionView } from "../event-description.view.ts";
import { hasOrders, orderHistory } from "../orders/orders.state.ts";
import "../language-toggle.view.ts";
import {
	archiveOpen,
	archiveOpenChoice,
	footerPinned,
	type LandingRow,
	landingPhase,
	lastSettledFrame,
	pastRows,
	upcomingRows,
} from "./landing.state.ts";

/** One auto-fit grid for every breakpoint and every count of poster, one
 * included, shared by the settled shelf and by the loading frame that stands in
 * for it, so the tiles the buyer waits on are the tiles that arrive — same
 * column track, same gap, same count.
 *
 * The 280px ceiling is a multi-column device — it stops a 3-up shelf stretching
 * its tiles — so it is spent only where multiple columns exist. At one column it
 * is pure subtraction, leaving 78px of a 390px phone empty, which is why the
 * narrow track carries no ceiling at all.
 *
 * Tracks fill from the column's own left edge, so a partly-filled row leaves its
 * slack on the right. Centring would split that slack either side and take the
 * first tile off the vertical every other container on this page starts on. */
const posterShelf =
	"grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] sm:grid-cols-[repeat(auto-fit,minmax(200px,280px))] items-start justify-start gap-6 md:gap-8";
const posterTile = "aspect-[2/3] w-full";

/** The archive's own track — the live shelf's device at roughly half its
 * measure (200/280 -> 140/180), so an ended event ranks below a live one
 * without inventing a second visual language for it. 140px is the floor at
 * which a posterless card still holds its date label above a three-line
 * `headline xs`. Density is a virtue inside a section the buyer chose to
 * open. */
const archiveShelf =
	"grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] sm:grid-cols-[repeat(auto-fit,minmax(140px,180px))] items-start justify-start gap-3 sm:gap-4";

@schmancyElement("landing-view")
export default class Landing extends LocalizeMixin(SchmancyElement) {
	/** Curtain and availability text ride the box above the poster/type branch, so the
	 * sold-out signal reads on both designs, and the type is foot-anchored so
	 * the availability line stays clear of it. The face never shapes its own box:
	 * the card that mounts it does, in both axes, so nothing here asks an
	 * engine to derive one axis from the other. `hero` is the page's only upcoming
	 * card: it scales the type, its tracking and the inset, and it loads a poster
	 * eagerly, because that poster is what the page paints first. Exactly one surface carries a poster — the
	 * poster itself: the card's box is the poster's own, the `<img>` covers it
	 * edge to edge, the face paints no ground of its own for anything to show
	 * through, and the card mounting it is chromeless and square, so no corner
	 * clips the artwork and no ground, edge or elevation rings it. The type
	 * design is the other side of that: there the card's surface is the thing,
	 * so it keeps the elevated rounded surface and the type sits on it.
	 *
	 * The refund chip rides the same box, and the card is itself the click
	 * target — so the chip stops both its click (or the buyer lands on the
	 * event instead) and its keydown, because the card's Enter/Space handler
	 * preventDefaults, which cancels the nested button's own activation and
	 * leaves it dead to the keyboard. Its fill is solid rather than a thinner
	 * glass tier because an arbitrary poster is a busy backdrop: measured over
	 * this deployment's own, the thin tiers put the label at 3.8:1, under the
	 * 4.5:1 floor, where solid reads 6.7:1.
	 *
	 * `past` is the card's membership in the archive, handed down rather than
	 * carried on the row, so there is no second representation of it to
	 * disagree with that membership. It draws the same curtain sold-out draws
	 * — this page's existing word for "you cannot get in" — and it suppresses
	 * the sold-out status, because availability on an ended event is no longer
	 * true or false and the word reads as an invitation to try again. */
	private cardFace(row: LandingRow, hero: boolean, past: boolean) {
		return html`
      <div
        ${curtain({ active: row.soldOut || past })}
        class="relative h-full w-full"
      >
        ${when(
					row.poster,
					() => html`<img
              src=${row.poster}
              alt=""
              width=${Event.POSTER_WIDTH}
              height=${Event.POSTER_HEIGHT}
              decoding="async"
              loading=${ifDefined(hero ? undefined : "lazy")}
              class="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03] group-has-[schmancy-button:hover]:scale-100"
            />`,
					() => html`
              <div
                class=${this.classMap({
									"flex h-full flex-col justify-end": true,
									"min-h-72 gap-4 p-6 sm:p-8 md:p-12": hero,
									"gap-3 p-4": !hero,
								})}
              >
                <div class="flex flex-col gap-1">
                  <schmancy-typography
                    type="label"
                    token=${hero ? "xl" : "md"}
                    transform="uppercase"
                    maxLines="1"
                    class=${this.classMap({
											"text-surface-onVariant": true,
											"tracking-widest": hero,
										})}
                  >
                    ${row.dateLabel}
                  </schmancy-typography>
                  <schmancy-typography
                    type=${hero ? "display" : "headline"}
                    token=${hero ? "md" : "xs"}
                    transform="uppercase"
                    maxLines="3"
                  >
                    ${row.name}
                  </schmancy-typography>
                </div>
                ${when(
									row.venue,
									() => html`
                    <schmancy-typography
                      type="body"
                      token=${hero ? "lg" : "sm"}
                      maxLines="1"
                      class="text-surface-onVariant"
                    >
                      ${row.venue}
                    </schmancy-typography>
                  `,
								)}
              </div>
            `,
				)}
        ${when(
					row.soldOut && !past,
					() => html`
            <schmancy-typography type="label" token="md" data-testid="sold-out-status" class="absolute inset-x-0 top-0 z-10 bg-surface-container px-3 py-2 text-surface-onVariant">
              ${this.t("storefront.landing.soldOutBadge")}
            </schmancy-typography>
          `,
				)}
        ${when(
					row.refundOpen,
					() => html`
            <schmancy-surface
              type="containerHighest"
              opacity="solid"
              elevated
              class="absolute bottom-3 left-3 z-10 max-w-[calc(100%-1.5rem)] overflow-hidden rounded-xl shadow-md"
            >
              <schmancy-button
                variant="text"
                color="neutral"
                size="sm"
                @click=${(e: Event) => {
									e.stopPropagation();
									area.push({
										area: "root",
										component: "refund",
										path: `${organizationPathPrefix.get()}/refund`,
										params: { e: row.id },
										historyStrategy: "push",
									});
								}}
                @keydown=${(e: KeyboardEvent) => e.stopPropagation()}
              >
                ${this.t("storefront.landing.refundButton")}
              </schmancy-button>
            </schmancy-surface>
          `,
				)}
      </div>
    `;
	}

	private openSettings() {
		show(AppSettings, { ariaLabel: this.t("settings.title") })
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	/** The poster design contains no text at all — its `<img>` is deliberately
	 * decorative — so an authored name is the only handle assistive tech has on
	 * that branch, and the sold-out word rides it or is lost with the rest. */
	private cardLabel(row: LandingRow, past: boolean) {
		return row.soldOut && !past
			? `${row.name}, ${row.dateLabel}, ${this.t("storefront.landing.soldOutBadge")}`
			: `${row.name}, ${row.dateLabel}`;
	}

	override render() {
		const name = brandName.get();
		// One minmax(0,1fr) column and four rows inside a definite h-dvh scrollport:
		// masthead, content, archive, footer. Definite, so a page shorter than the
		// viewport still ends on its floor; dvh, not svh, or the footer strands short
		// of that floor once mobile chrome collapses.
		//
		// The content row's floor is max-content, never `auto`. A card's
		// overflow-hidden zeroes its automatic minimum height, so an `auto` track
		// shrinks to whatever height the definite grid has left while the card keeps
		// its own and paints over the rows below. With that floor a page taller than
		// the viewport scrolls instead, every row keeping its order.
		//
		// Which row takes the free height is all the two cases disagree about.
		// Without an archive it is the content row. With an archive it is the
		// archive's own row, BELOW the drawer: the bar sits directly under the
		// content at its own top margin and stays there when it opens, rather than
		// travelling up out from under the cursor that opened it.
		//
		// Nothing centres horizontally. Every item stretches to that one column,
		// so masthead, shelf, archive and footer all start where the scrollport's
		// own inline padding puts them, and that padding is the page's single
		// vertical. The column track is explicit for two reasons: an implicit
		// `auto` column floors at its child's min-content, so a child wider than
		// the row grows the track, where a 1fr floor of 0 holds it to the column;
		// and a shrink-to-fit item makes `repeat(auto-fit, …)` repeat exactly once
		// against an indefinite width, which renders a shelf as one 280px column
		// at every viewport and every count.
		//
		// The posterless card centres VERTICALLY with `my-auto` rather than the
		// row's own align-items, because centred alignment on a scroll container
		// puts the top of any overflowing content out of reach where auto margins
		// collapse to zero. With an archive below it holds `self-start`, since the
		// free height is no longer its row's.
		//
		// The loading frame renders the branch the settled frame is about to render
		// rather than a shape of its own, so nothing resizes when the list arrives.
		const phase = landingPhase.get();
		const past = pastRows.get();
		const hasPast = past.length > 0;
		// The count rides the sentence rather than a badge beside it, so the
		// disclosure's accessible name is a whole phrase and never a floating
		// number — and the same resolved string names the group it opens, which
		// native <details> does not announce on its own.
		const archiveLabel =
			past.length === 1
				? this.t("storefront.landing.pastOne")
				: this.t("storefront.landing.pastOther", { count: past.length });
		return html`
      <div class="grid h-dvh grid-cols-[minmax(0,1fr)] grid-rows-[minmax(0,1fr)]">
      <div
        ${overflowWithin()}
        class=${this.classMap({
					"mx-auto grid w-full max-w-6xl grid-cols-[minmax(0,1fr)] px-4 sm:px-6 md:px-8": true,
					"grid-rows-[auto_minmax(max-content,1fr)_auto_auto]": !hasPast,
					"grid-rows-[auto_max-content_minmax(max-content,1fr)_auto]": hasPast,
				})}
      >
      <!-- The masthead rides INSIDE the scroll box as its first row, pinned.
           It paints the page ground so posters disappear cleanly beneath it
           without turning the header into a separate glass panel. Like every
           other row of this grid it spans the column, so the mark and the
           orders action start and end on the page's own two verticals.

           The landing page mounts no brand-bar, so it carries the language
           switch and the way back to a buyer's own orders itself. The entry is absent rather than
           empty-handed: it opens a list, and
           offering it to a browser with no orders is a dead end offered as a
           destination — but a failed listener is not an empty list, and
           hiding the only way to the error frame is the one state that
           strands the buyer. -->
      <div
		class="sticky top-0 z-10 flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-1 bg-surface-lowest py-4 sm:py-6 md:py-8"
      >
        <!-- The mark is this page's only way into settings, so it names the
             action rather than the artwork: its own shadow's role="img" carries
             the brand name, which says nothing about what activating it does. -->
        <brand-logo
          named
          class="h-9 w-auto min-w-0 max-w-full md:h-10"
          role="button"
          tabindex="0"
          aria-label=${this.t("settings.title")}
          @click=${() => this.openSettings()}
          @keydown=${(e: KeyboardEvent) => {
						// A native button splits its two activation keys, and so does this:
						// Enter fires on the way down, Space on the way up. keydown alone
						// would auto-repeat, and `show()` mints a fresh overlay AND a fresh
						// history entry per call — so a held Space would stack both at the
						// OS repeat rate. Space still preventDefaults here, on the down
						// stroke, because that is where the page would scroll.
						if (e.key === " ") e.preventDefault();
						else if (e.key === "Enter") this.openSettings();
					}}
          @keyup=${(e: KeyboardEvent) => {
						if (e.key === " ") this.openSettings();
					}}
        ></brand-logo>
        <div class="flex min-w-0 items-center gap-1">
          ${when(
						hasOrders.value || orderHistory.value.status === "error",
						() => html`
            <schmancy-button
              variant="outlined"
              color="neutral"
              size="sm"
              compact
              title=${this.t("storefront.orders.entryLabel")}
              aria-label=${this.t("storefront.orders.entryLabel")}
              class="sm:hidden"
              @click=${() =>
								area.push({
									area: "root",
									component: "orders",
									path: `${organizationPathPrefix.get()}/orders`,
									historyStrategy: "push",
								})}
            >
              <schmancy-icon slot="prefix">confirmation_number</schmancy-icon>
            </schmancy-button>
            <schmancy-button
              variant="outlined"
              color="neutral"
              size="sm"
              class="hidden sm:block"
              @click=${() =>
								area.push({
									area: "root",
									component: "orders",
									path: `${organizationPathPrefix.get()}/orders`,
									historyStrategy: "push",
								})}
            >
              <schmancy-icon slot="prefix">confirmation_number</schmancy-icon>
              ${this.t("storefront.orders.entryLabel")}
            </schmancy-button>
          `,
					)}
		  <language-toggle class="self-stretch"></language-toggle>
        </div>
        ${when(
					workspaceCard.value.workspace?.payment.mode === "sandbox",
					() =>
						html`<schmancy-typography type="label" token="sm" class="w-full text-warning-default">${this.t("storefront.eventPage.payArea.testModeBanner")}</schmancy-typography>`,
				)}
      </div>
        ${choose(phase, [
					[
						"loading",
						() => {
							const frame = lastSettledFrame.value;
							// Each branch stands in for the settled branch it precedes,
							// carrying what that branch's geometry is known to be: the
							// shelf's own column track and count. A single poster's tile
							// goes square against its chromeless card, outranking
							// schmancy-skeleton's 10px `:host` radius; a shelf frame knows
							// only its count, not which of its tiles arrive with a poster
							// and go square, so its tiles hedge at `rounded-2xl`.
							//
							// `hasPast` rides the frame for the posterless card, which
							// picks its vertical placement off the same fact.
							return frame.kind === "single-text"
								? html`
                  <schmancy-skeleton
                    role="status"
                    aria-busy="true"
                    aria-label=${this.t("storefront.landing.loadingAriaLabel")}
                    class=${this.classMap({
											"min-h-72 w-full rounded-2xl": true,
											"my-auto": !frame.hasPast,
											"self-start": frame.hasPast,
										})}
                  ></schmancy-skeleton>
                `
								: html`
                  <div
                    role="status"
                    aria-busy="true"
                    aria-label=${this.t("storefront.landing.loadingAriaLabel")}
                    class="${posterShelf} self-start"
                  >
                    ${Array.from(
											{ length: frame.kind === "shelf" ? frame.count : 1 },
											() => html`
                        <schmancy-skeleton
                          class=${this.classMap({
														[posterTile]: true,
														"rounded-none": frame.kind === "single-poster",
														"rounded-2xl": frame.kind === "shelf",
													})}
                        ></schmancy-skeleton>
                      `,
										)}
                  </div>
                `;
						},
					],
					[
						"empty",
						() => html`
              <div class="my-auto flex flex-col items-center gap-3 text-center">
                <schmancy-icon class="text-4xl text-surface-onVariant"
                  >event_busy</schmancy-icon
                >
                <schmancy-typography type="headline" token="sm"
                  >${this.t("storefront.landing.emptyTitle")}</schmancy-typography
                >
                <schmancy-typography
                  type="body"
                  token="md"
                  class="max-w-sm text-surface-onVariant"
                >
                  ${this.t("storefront.landing.emptyBody", { name })}
                </schmancy-typography>
              </div>
            `,
					],
					// Preserve the content row so the archive keeps its grid track.
					["past-only", () => html`<div></div>`],
					[
						"populated",
						() => {
							const rows = upcomingRows.get();
							const [first] = rows;
							return first && rows.length === 1 && first.poster === null
								? html`
                    <schmancy-card
                      type="elevated"
                      interactive
                      role="button"
                      aria-label=${this.cardLabel(first, false)}
                      class=${this.classMap({
												"group w-full overflow-hidden rounded-2xl": true,
												"my-auto": !hasPast,
												"self-start": hasPast,
											})}
                      @click=${() =>
												area.push({
													area: "root",
													component: "/:eventId",
													params: { eventId: first.id },
													path: `${organizationPathPrefix.get()}/${encodeURIComponent(first.id)}`,
													historyStrategy: "push",
												})}
                    >
                      ${this.cardFace(first, true, false)}
                    </schmancy-card>
                  `
								: html`
                    <div class="${posterShelf} self-start">
                      ${repeat(
												rows,
												(row) => row.id,
												(row) => html`
                          <schmancy-card
                            type=${row.poster !== null ? "chromeless" : "elevated"}
                            interactive
                            role="button"
                            aria-label=${this.cardLabel(row, false)}
                            class=${this.classMap({
															[`${posterTile} group overflow-hidden`]: true,
															"rounded-2xl": row.poster === null,
														})}
                            @click=${() =>
															area.push({
																area: "root",
																component: "/:eventId",
																params: { eventId: row.id },
																path: `${organizationPathPrefix.get()}/${encodeURIComponent(row.id)}`,
																historyStrategy: "push",
															})}
                          >
                            ${this.cardFace(row, rows.length === 1, false)}
                          </schmancy-card>
                        `,
											)}
                    </div>
                  `;
						},
					],
				])}
        <!-- The archive: its own grid row, never a second child of the
             content row above. A sibling placed there flows into the next
             implicit row anyway, so "inside" is a fiction — and wrapping
             shelf and archive in one grid item would lend the archive that
             branch's placement.

             It spans the scrollport's own column, exactly as the loading and
             empty branches do, and it is held at the top of the row that
             carries the page's free height — so the bar rests one top margin
             below the shelf, and the slack falls between the drawer and the
             footer rather than above the bar. -->
      ${when(
				hasPast,
				() => html`
          <schmancy-details
            type="transparent"
            indicator-placement="start"
            summary-padding="py-3"
            content-padding="pt-1 pb-4"
            class="mt-6 w-full self-start"
            ?open=${archiveOpen.get()}
            @toggle=${(e: CustomEvent<{ open: boolean }>) => {
							// The native toggle fires on a programmatic open too, so
							// without this the `?open` binding echoes back into the cell
							// that drove it and overwrites the derived default with
							// itself.
							if (e.detail.open === archiveOpen.get()) return;
							archiveOpenChoice.replace(e.detail.open);
						}}
          >
            <!-- The card's own date-label treatment one token larger, so the
                 bar reads as page furniture in the voice this page already
                 speaks. The one-line cap is structural, not cosmetic: a
                 wrapping summary would make this row's collapsed height a
                 function of its own width. -->
            <schmancy-typography
              slot="summary"
              type="label"
              token="lg"
              transform="uppercase"
              maxLines="1"
              class="text-surface-onVariant"
            >
              ${archiveLabel}
            </schmancy-typography>
            <!-- role="group" with the summary's own words: native <details>
                 does not reliably tell a screen reader what it just entered. -->
            <div
              role="group"
              aria-label=${archiveLabel}
              class="${archiveShelf}"
            >
              ${repeat(
								past,
								(row) => row.id,
								(row) => html`
                  <schmancy-card
                    type=${row.poster !== null ? "chromeless" : "elevated"}
                    interactive
                    role="button"
                    aria-label=${this.cardLabel(row, true)}
                    class=${this.classMap({
											[`${posterTile} group overflow-hidden`]: true,
											"rounded-2xl": row.poster === null,
										})}
                    @click=${() =>
											show(EventDescriptionView, {
												ariaLabel: row.name,
												props: {
													name: row.name,
													dateLabel: row.dateLabel,
													poster: row.poster,
													venue: row.venue,
													description: row.description ?? "",
												},
											})
												.pipe(takeUntil(this.disconnecting))
												.subscribe()}
                  >
                    ${this.cardFace(row, false, true)}
                  </schmancy-card>
                `,
							)}
            </div>
          </schmancy-details>
        `,
			)}
      <!-- Sticky as the scroll box's last child, which is what puts the bar
             on the viewport floor at every scroll position AND leaves it in
             flow, so whatever is above it comes to rest above it rather than
             under it; fixed positioning would buy the same pinning by leaving
             the flow. Only a shelf of two or more events pins it. Every other
             page rests it below its content: pinned over a viewport shorter
             than that page, the plate covers the event's own card or the
             archive. The bar carries no ground of its own — legal-footer's own plate is the
             page's ground tone, opaque, so the cards end at it — and the top
             margin is the gap that keeps the row above from stopping flush
             against that plate. -->
        <legal-footer
          class=${this.classMap({
						"mt-6": true,
						"sticky bottom-0 z-10": footerPinned.get(),
					})}
        ></legal-footer>
      </div>
      </div>
    `;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"landing-view": Landing;
	}
}
